import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import MemberAvatar from "@/components/MemberAvatar";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Mail, Phone, Anchor, Cake, Sailboat, Crown, Flag, Ban } from "lucide-react";
import Crest, { CrestBadge } from "@/components/Crest";
import { OrderBadge } from "@/components/Beret";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ReportDialog, useBlock } from "@/components/Safety";

const FRIGATES = ["Cherokee", "Cumberland", "Pelican"];

export default function Directory() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [frig, setFrig] = useState("All");
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const { user } = useAuth();
  const { isBlocked, block, unblock } = useBlock();

  const load = (query = "", frigate = "All") => {
    const params = { q: query };
    if (frigate !== "All") params.frigate = frigate;
    api.get("/members", { params }).then((r) => setMembers(r.data.members));
  };
  useEffect(() => { const t = setTimeout(() => load(q, frig), 250); return () => clearTimeout(t); }, [q, frig]);

  return (
    <div>
      <PageHeader eyebrow="The Crew" title="Member Directory"
        description="Every brother across the Galleon — deck names, positions, and frigates." />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, deck, position…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="directory-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["All", ...FRIGATES].map((f) => (
            <button key={f} onClick={() => setFrig(f)} data-testid={`frigate-filter-${f}`}
              className={`rounded-full px-4 py-1.5 text-sm font-600 transition-colors ${frig === f ? "bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {members.map((m) => (
          <Card key={m.id} onClick={() => setSelected(m)} className="gold-ring glass relative cursor-pointer p-5 text-center" data-testid={`member-card-${m.id}`}>
            <div className="absolute right-3 top-3"><Crest position={m.position} size={30} /></div>
            <Avatar className="mx-auto h-20 w-20 ring-2 ring-[hsl(var(--gold))]/30">
              <MemberAvatar name={m.name} photo={m.photo_path} className="h-20 w-20" />
            </Avatar>
            <h3 className="mt-3 font-display text-lg font-700">{m.name}{m.position === "Frigate Captain" && <Crown className="ml-1 inline h-4 w-4 text-[hsl(var(--gold))]" />}</h3>
            {m.deck_name && <p className="text-xs italic text-muted-foreground">"{m.deck_name}"</p>}
            {CrestBadge({ position: m.position, className: "mt-2" }) || <Badge variant="outline" className="mt-2 border-[hsl(var(--water))]/40 text-[hsl(var(--water))]">{m.position}</Badge>}
            {m.order && <div className="mt-2 flex justify-center"><OrderBadge order={m.order} testid={`member-order-${m.id}`} /></div>}
            <p className="mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <Sailboat className="h-3.5 w-3.5" />{m.frigate || "Galleon"}
            </p>
          </Card>
        ))}
      </div>
      {members.length === 0 && <p className="text-sm text-muted-foreground">No members found.</p>}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="glass">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="sr-only">{selected.name}</DialogTitle></DialogHeader>
              <div className="flex flex-col items-center text-center">
                <Avatar className="h-24 w-24 ring-2 ring-[hsl(var(--gold))]/40">
                  <MemberAvatar name={selected.name} photo={selected.photo_path} className="h-24 w-24" />
                </Avatar>
                <h2 className="mt-3 font-display text-2xl font-700">{selected.name}</h2>
                {selected.deck_name && <p className="text-sm italic text-muted-foreground">Deck name: "{selected.deck_name}"</p>}
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                  <Crest position={selected.position} size={44} />
                  <Badge className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]">{selected.position}</Badge>
                  <Badge variant="outline" className="border-[hsl(var(--water))]/40 text-[hsl(var(--water))]"><Sailboat className="mr-1 h-3 w-3" />{selected.frigate || "Galleon"}</Badge>
                </div>
                {selected.order && <OrderBadge order={selected.order} className="mt-2" testid="member-dialog-order" />}
                {selected.bio && <p className="mt-4 text-sm text-muted-foreground">{selected.bio}</p>}
                <div className="mt-5 w-full space-y-2 text-left text-sm">
                  <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[hsl(var(--water))]" /> {selected.email}</div>
                  {selected.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[hsl(var(--water))]" /> {selected.phone}</div>}
                  {selected.birthday && <div className="flex items-center gap-2"><Cake className="h-4 w-4 text-pink-500" /> {selected.birthday}</div>}
                </div>
                {selected.id !== user?.id && (
                  <div className="mt-5 flex w-full gap-2" data-testid="member-safety-actions">
                    <Button size="sm" variant="outline" className="flex-1 gold-ring" onClick={() => setReport({ type: "member", id: selected.id })} data-testid={`report-member-${selected.id}`}><Flag className="mr-1.5 h-3.5 w-3.5" />Report</Button>
                    {isBlocked(selected.id)
                      ? <Button size="sm" variant="outline" className="flex-1" onClick={() => unblock(selected.id, selected.name)} data-testid={`unblock-member-${selected.id}`}><Ban className="mr-1.5 h-3.5 w-3.5" />Unblock</Button>
                      : <Button size="sm" variant="outline" className="flex-1 border-destructive/40 text-destructive hover:bg-destructive hover:text-white" onClick={() => block(selected.id, selected.name)} data-testid={`block-member-${selected.id}`}><Ban className="mr-1.5 h-3.5 w-3.5" />Block</Button>}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ReportDialog target={report} onClose={() => setReport(null)} />
    </div>
  );
}
